import {Injectable, PLATFORM_ID, Inject, Optional} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {HttpClient, HttpEvent, HttpEventType, HttpHeaders} from '@angular/common/http';
import {Observable} from 'rxjs';
import {environment} from "../../environments/environment";
import {get_school_id} from "./school.service";
import {REQUEST} from '@nguniversal/express-engine/tokens';
import {Request} from 'express';

@Injectable({providedIn: 'root'})
export class ApiService {

	// Token can be set directly (for backward compatibility)
	// But getAuthToken() is the preferred way to get the current token
	private _token: string = null;
	public lang: string = 'tc';

	public common_extra_headers: any = {};
	private isBrowser: boolean;

	constructor(
		@Inject(PLATFORM_ID) platformId: Object,
		@Optional() @Inject(REQUEST) private request: Request,
		private http: HttpClient
	) {
		this.isBrowser = isPlatformBrowser(platformId);
		this.common_extra_headers.school_id = `${get_school_id()}`;
		
		// On server, try to get token from cookie in request
		if (!this.isBrowser && this.request?.headers?.cookie) {
			this.initTokenFromCookie();
		}
	}
	
	/**
	 * Setter for token (backward compatibility with AuthService)
	 */
	set token(value: string) {
		this._token = value;
	}
	
	/**
	 * Getter for token - always returns the most current token
	 * On browser: reads from localStorage if _token is null
	 * On server: uses _token (set from cookie)
	 */
	get token(): string {
		// If token is already set, use it
		if (this._token) {
			return this._token;
		}
		
		// On browser, try to get from localStorage
		if (this.isBrowser && typeof localStorage !== 'undefined') {
			const tokenKey = `online_shop_${get_school_id()}_token`;
			const storedToken = localStorage.getItem(tokenKey);
			if (storedToken) {
				this._token = storedToken;
				return storedToken;
			}
		}
		
		return null;
	}
	
	/**
	 * Initialize token from cookie on server-side
	 */
	private initTokenFromCookie(): void {
		if (!this.request?.headers?.cookie) return;
		
		const tokenKey = `online_shop_${get_school_id()}_token`;
		const cookies = this.request.headers.cookie.split(';');
		
		for (const cookie of cookies) {
			const [name, ...valueParts] = cookie.split('=');
			if (name.trim() === tokenKey) {
				const value = valueParts.join('=').trim();
				if (value) {
					this._token = decodeURIComponent(value);
				}
				break;
			}
		}
	}

	// get() should never use, use post() instead
	public get(url, data = {}) {
		return new Promise(async (resolve, reject) => {
			let api_url = this.processUrl(url);
			// console.log(api_url);

			// change to fetch
			let response = await fetch(api_url, {
				method: 'GET',
				headers: {
					'Authorization': `Bearer ${this.token}`,
					'lang': this.lang,
					...this.common_extra_headers
				}
			});
			resolve(await response.json());
		});
	}

	public post(endpoint, data = null, keepalive = false, debugCode?):Promise<any> {
		return new Promise(async (resolve, reject) => {
			try {
				let payload = [data]
				if (debugCode) {
					payload.push(debugCode)
				}
				// console.log('post', this.common_extra_headers)
				let response = await fetch(environment.apiUrl, {
					method: 'POST',
					headers: {
						'Authorization': `Bearer ${this.token}`,
						// 'Content-Type': 'application/json;charset=UTF-8',
						'Content-Type': 'application/json',
						// set lang
						// 'lang': this.lang,
						// ...this.common_extra_headers
					},
					body: JSON.stringify({
						api: endpoint,
						json: JSON.stringify(payload)
					}),
					keepalive: keepalive
				});

				// Check if the response is ok (status 200-299)
				if (!response.ok) {
					reject(new Error(`HTTP error! status: ${response.status}`));
					return;
				}

				let jsonResponse = await response.json();

				// if error, reject the promise (due to ROWebServer isn't returning non-200 status code for any data fetching error)
				if(jsonResponse.status === 'error') reject(jsonResponse);

				resolve(jsonResponse);
			} catch (error) {
				// This will catch network errors, JSON parsing errors, etc.
				reject(error);
			}
		});
	}

	public directUpload(
		endpoint: string, 
		files: File[], 
		additionalData: any = {}, 
		onProgress?: (progress: number) => void
	): Promise<any> {
		return new Promise((resolve, reject) => {
			const formData = new FormData();
			
			// Add files to form data
			files.forEach((file, index) => {
				// Use Filedata[] instead of Filedata to support multiple files (also supports single file)
				formData.append('Filedata[]', file);
			});
			
			// Add additional data to form data
			Object.keys(additionalData).forEach(key => {
				formData.append(key, additionalData[key]);
			});

			// Set up headers
			let headers = new HttpHeaders();
			if (this.token) {
				headers = headers.set('Authorization', `Bearer ${this.token}`);
			}

			// Use Angular HttpClient with progress tracking
			this.http.post(endpoint, formData, {
				headers,
				reportProgress: true,
				observe: 'events'
			}).subscribe({
				next: (event: HttpEvent<any>) => {
					if (event.type === HttpEventType.UploadProgress && onProgress) {
						if (event.total) {
							const progress = Math.round((event.loaded / event.total) * 100);
							onProgress(progress);
						}
					} else if (event.type === HttpEventType.Response) {
						const response = event.body;
						
						// Handle API-level errors (status: "error" in response body)
						if (response && response.status === 'error') {
							reject(response);
						} else {
							resolve(response);
						}
					}
				},
				error: (error) => {
					// Handle HTTP errors and API errors
					if (error.error && error.error.status === 'error') {
						reject(error.error);
					} else {
						reject(new Error('Network error occurred'));
					}
				}
			});
		});
	}

	public processUrl(url) {
		return `${environment.apiUrl}${url}`;
	}

}
